import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { computeTotals, fmtCurrency, fmtHours, fmtPercent } from "../lib/calculations";
import {
  buildEmptyDmMatrix, buildEmptyIllustrations, DEFAULT_ROLES, DM_TYPES, findCurrency, FX_SUPPORTED_CODES,
} from "../lib/s1000dData";
import { fetchFxRates, convertAmount } from "../lib/fxClient";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ArrowLeftRight, RefreshCw, AlertTriangle } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
function hydrate(doc) {
  if (!doc) return null;
  return {
    projectName: doc.projectName || "Untitled",
    currency: doc.currency || findCurrency("USD"),
    overheadPercent: Number(doc.overheadPercent) || 0,
    contingencyPercent: Number(doc.contingencyPercent) || 0,
    dmMatrix: { ...buildEmptyDmMatrix(), ...(doc.dmMatrix || {}) },
    illustrations: { ...buildEmptyIllustrations(), ...(doc.illustrations || {}) },
    roles: (doc.roles && doc.roles.length > 0) ? doc.roles : DEFAULT_ROLES.map((r) => ({ ...r })),
  };
}

function Row({ label, a, b, formatA, formatB, fallback = false }) {
  const numA = typeof a === "number" ? a : 0;
  const numB = typeof b === "number" ? b : 0;
  const diff = numB - numA;
  const arrow = diff === 0 ? "·" : diff > 0 ? "▲" : "▼";
  const liveColor = diff === 0 ? "text-gray-400" : diff > 0 ? "text-[#FF2400]" : "text-[#15803D]";
  // In FX-fallback mode the delta is unreliable, desaturate it.
  const arrowColor = fallback ? "text-gray-400" : liveColor;
  return (
    <tr className="border-b border-gray-200 hover:bg-[#F9FAFB]">
      <td className="py-2 px-3 font-semibold text-black">{label}</td>
      <td className="py-2 px-3 font-mono text-right">{formatA ? formatA(numA) : numA}</td>
      <td className="py-2 px-3 font-mono text-right">{formatB ? formatB(numB) : numB}</td>
      <td className={`py-2 px-3 font-mono text-right text-xs ${arrowColor} ${fallback ? "italic" : ""}`}>
        {arrow} {formatA ? formatA(Math.abs(diff)) : Math.abs(diff)}
      </td>
    </tr>
  );
}

export default function CompareTab() {
  const { t } = useTranslation();
  const { savedList, fetchList } = useEstimate();
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [fxOn, setFxOn] = useState(false);
  const [fx, setFx] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState("");

  useEffect(() => { fetchList(); }, [fetchList]);

  const a = useMemo(() => hydrate(savedList.find((p) => p.id === aId)), [savedList, aId]);
  const b = useMemo(() => hydrate(savedList.find((p) => p.id === bId)), [savedList, bId]);

  const tA = a ? computeTotals(a) : null;
  const tB = b ? computeTotals(b) : null;

  const codeA = a?.currency?.code || "USD";
  const codeB = b?.currency?.code || "USD";
  // True when the two estimates use different currencies and at least one
  // is not in the ECB live-rates set — Compare conversion falls back to 1:1.
  const fxFallback = codeA !== codeB &&
    (!FX_SUPPORTED_CODES.includes(codeA) || !FX_SUPPORTED_CODES.includes(codeB));
  const symA = a?.currency?.symbol || "$";

  // Load FX rates with codeA as base whenever fx is toggled on, or A changes
  useEffect(() => {
    if (!fxOn || !a) return;
    let cancelled = false;
    setFxLoading(true);
    setFxError("");
    (async () => {
      try {
        const data = await fetchFxRates(codeA);
        if (!cancelled) setFx(data);
      } catch {
        if (!cancelled) setFxError("Could not fetch FX rates. Showing native currencies.");
      } finally {
        if (!cancelled) setFxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fxOn, codeA, a]);

  const conv = (amount) => {
    if (!fxOn || !fx || codeA === codeB) return amount;
    return convertAmount(amount, fx.rates, codeB, codeA);
  };
  const symB = b?.currency?.symbol || "$";
  const fmtA = (v) => fmtCurrency(v, symA);
  const fmtBnative = (v) => fmtCurrency(v, symB);
  // When FX is on, B values are converted to A's currency for both display and delta math.
  const symBdisplay = fxOn && fx ? symA : symB;
  const bVal = (v) => (fxOn && fx ? conv(v) : v);
  const fmtBdisplay = (v) => fmtCurrency(v, symBdisplay);

  const dmChartData = DM_TYPES.map((t) => ({
    name: t.name,
    A: tA?.perTypeHours[t.code] || 0,
    B: tB?.perTypeHours[t.code] || 0,
  })).filter((d) => d.A > 0 || d.B > 0);

  const rate = fx?.rates?.[codeB];

  return (
    <section className="space-y-6" data-testid="compare-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("compare.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("compare.title")}
            <HelpIconButton anchor="ch5" testId="help-icon-compare" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("compare.description")}
          </p>
        </div>
        {a && b && codeA !== codeB && (
          <div className="border border-gray-300 bg-white p-4 min-w-[260px]" data-testid="fx-control">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Currency Normalisation
            </p>
            <div className="flex items-center justify-between mt-2 gap-3">
              <button
                onClick={() => setFxOn((v) => !v)}
                data-testid="fx-toggle"
                className={
                  "rounded-none px-3 py-2 font-bold text-xs transition-colors inline-flex items-center gap-2 " +
                  (fxOn
                    ? "bg-[#002FA7] text-white hover:bg-black"
                    : "bg-white border border-gray-300 text-black hover:bg-gray-100")
                }
              >
                <ArrowLeftRight size={12} strokeWidth={2} />
                {fxOn ? `Showing B as ${codeA}` : `Show B as ${codeA}`}
              </button>
              {fxLoading && <RefreshCw size={14} className="animate-spin text-gray-400" />}
            </div>
            {fxOn && rate && (
              <p className="font-mono text-[10px] text-gray-500 mt-2" data-testid="fx-rate-info">
                1 {codeA} = {rate.toFixed(4)} {codeB} · source {fx?.source}
              </p>
            )}
            {fxError && <p className="text-[10px] text-[#FF2400] mt-2">{fxError}</p>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-300 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Estimate A</p>
          <select
            data-testid="compare-select-a"
            value={aId}
            onChange={(e) => setAId(e.target.value)}
            className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full bg-white"
          >
            <option value="">— Select an estimate —</option>
            {savedList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.projectName} · {p.currency?.code || "USD"}
              </option>
            ))}
          </select>
        </div>
        <div className="bg-white border border-gray-300 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Estimate B</p>
          <select
            data-testid="compare-select-b"
            value={bId}
            onChange={(e) => setBId(e.target.value)}
            className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full bg-white"
          >
            <option value="">— Select an estimate —</option>
            {savedList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.projectName} · {p.currency?.code || "USD"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!a || !b ? (
        <div className="bg-white border border-gray-300 p-10 text-center text-gray-400 text-sm" data-testid="compare-empty">
          Pick an estimate in both selectors to see the comparison.
        </div>
      ) : (
        <>
          {(() => {
            const aLive = FX_SUPPORTED_CODES.includes(codeA);
            const bLive = FX_SUPPORTED_CODES.includes(codeB);
            // Only warn when currencies differ and at least one is non-FX.
            if (codeA === codeB || (aLive && bLive)) return null;
            const offenders = [
              ...(!aLive ? [`A · ${codeA}`] : []),
              ...(!bLive ? [`B · ${codeB}`] : []),
            ].join(", ");
            return (
              <div
                data-testid="compare-fx-fallback-banner"
                className="bg-[#FFFBEB] border-l-4 border-[#CA8A04] px-4 py-3 flex items-start gap-3"
              >
                <AlertTriangle size={16} className="text-[#CA8A04] mt-0.5 shrink-0" />
                <div className="text-xs text-[#78350F] leading-relaxed">
                  <p className="font-bold uppercase tracking-wider text-[10px] text-[#CA8A04] mb-0.5">
                    {t("compare.fxFallback.title", { defaultValue: "FX 1:1 fallback in play" })}
                  </p>
                  <p>
                    <span className="font-bold">{offenders}</span>{" "}
                    {t("compare.fxFallback.body", {
                      defaultValue: "is not part of the ECB live-rates set. Cross-currency conversion will fall back to 1:1 — totals may match exactly even though the underlying figures differ. Switch to an FX-supported currency for an apples-to-apples comparison.",
                    })}
                  </p>
                </div>
              </div>
            );
          })()}
          {/* Compute fallback flag once for the diff table styling */}
          <div className="bg-white border border-gray-300" data-testid="compare-table">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
                  <th className="py-3 px-3 text-left">Metric</th>
                  <th className="py-3 px-3 text-right">A · {a.projectName} ({codeA})</th>
                  <th className="py-3 px-3 text-right">
                    B · {b.projectName} ({fxOn && fx ? `${codeB}→${codeA}` : codeB})
                  </th>
                  <th className="py-3 px-3 text-right" data-testid="compare-delta-header">
                    Δ (B − A)
                    {fxFallback && (
                      <span
                        className="ml-1 font-mono normal-case text-[#CA8A04]"
                        data-testid="compare-delta-fallback-tag"
                        title={t("compare.fxFallback.deltaTitle", { defaultValue: "Delta unreliable — FX 1:1 fallback in play" })}
                      >
                        (1:1)
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <Row label="Total Data Modules" a={tA.totalDms} b={tB.totalDms} fallback={fxFallback} />
                <Row label="Total Illustrations" a={tA.totalIllus} b={tB.totalIllus} fallback={fxFallback} />
                <Row label="DM Authoring Hours" a={tA.dmHours} b={tB.dmHours} formatA={fmtHours} formatB={fmtHours} fallback={fxFallback} />
                <Row label="Illustration Hours" a={tA.illusHours} b={tB.illusHours} formatA={fmtHours} formatB={fmtHours} fallback={fxFallback} />
                <Row label="Total Hours" a={tA.totalHours} b={tB.totalHours} formatA={fmtHours} formatB={fmtHours} fallback={fxFallback} />
                <Row label="Blended Rate / hr" a={tA.blendedRate} b={bVal(tB.blendedRate)} formatA={fmtA} formatB={fmtBdisplay} fallback={fxFallback} />
                <Row label="Base Cost" a={tA.baseCost} b={bVal(tB.baseCost)} formatA={fmtA} formatB={fmtBdisplay} fallback={fxFallback} />
                <Row
                  label={`Overhead (A:${a.overheadPercent || 0}% · B:${b.overheadPercent || 0}%)`}
                  a={tA.overheadCost} b={bVal(tB.overheadCost)} formatA={fmtA} formatB={fmtBdisplay} fallback={fxFallback} />
                <Row
                  label={`Contingency (A:${a.contingencyPercent || 0}% · B:${b.contingencyPercent || 0}%)`}
                  a={tA.contingencyCost} b={bVal(tB.contingencyCost)} formatA={fmtA} formatB={fmtBdisplay} fallback={fxFallback} />
              </tbody>
              <tfoot>
                <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
                  <td className="py-3 px-3">Grand Total</td>
                  <td className="py-3 px-3 font-mono text-right normal-case" data-testid="compare-a-grand">
                    {fmtA(tA.grandTotal)}
                  </td>
                  <td className="py-3 px-3 font-mono text-right normal-case" data-testid="compare-b-grand">
                    {fmtBdisplay(bVal(tB.grandTotal))}
                    {fxOn && fx && (
                      <div className="text-[9px] font-normal text-gray-400 normal-case font-mono mt-0.5">
                        native {fmtBnative(tB.grandTotal)}
                      </div>
                    )}
                  </td>
                  <td className={"py-3 px-3 font-mono text-right normal-case " + (fxFallback ? "text-gray-500 italic" : "")} data-testid="compare-delta-grand">
                    {(() => {
                      const bConverted = bVal(tB.grandTotal);
                      const delta = bConverted - tA.grandTotal;
                      const pct = tA.grandTotal > 0 ? (delta / tA.grandTotal) * 100 : 0;
                      return (
                        <>
                          {delta >= 0 ? "+" : "−"}
                          {fmtCurrency(Math.abs(delta), symA)} ({fmtPercent(pct)})
                        </>
                      );
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bg-white border border-gray-300 p-5" data-testid="compare-dm-chart">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Hours per DM Type — A vs B
            </p>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              Effort Comparison
            </h3>
            <div className="h-96 mt-4">
              {dmChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  Neither estimate has any DM hours yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                  <BarChart data={dmChartData} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid stroke="#E5E7EB" horizontal={false} />
                    <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                    <YAxis type="category" dataKey="name" stroke="#6B7280" tick={{ fontSize: 10 }} width={160} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="A" fill="#002FA7" />
                    <Bar dataKey="B" fill="#FF2400" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
